import React, { useEffect } from 'react';
import DynamicInsightField from './DynamicInsightField';
import { InsightItem, Opportunity, InternalOwnership, SuccessMetric } from '@/types/journey';
import EditableField from '../stage/EditableField';

interface InsightsContentProps {
  gaps: string;
  opportunities: string; // Kept for backward compatibility
  insightItems: InsightItem[];
  onGapsChange: (value: string) => void;
  onOpportunitiesChange?: (value: string) => void; // Kept for backward compatibility
  onInsightUpdate: (insightItems: InsightItem[]) => void;
}

const InsightsContent: React.FC<InsightsContentProps> = ({
  gaps,
  opportunities,
  insightItems,
  onGapsChange,
  onOpportunitiesChange,
  onInsightUpdate
}) => {
  // Convert legacy opportunities to insightItems format if needed
  useEffect(() => {
    // Skip if we already have insightItems
    if (Array.isArray(insightItems) && insightItems.length > 0) {
      // Check if we have all three required insight types
      const hasOpportunities = insightItems.some(item => item.type === 'opportunities');
      const hasInternalOwnership = insightItems.some(item => item.type === 'internalOwnership');
      const hasSuccessMetrics = insightItems.some(item => item.type === 'successMetrics');
      
      // If we have all three types, no need to initialize
      if (hasOpportunities && hasInternalOwnership && hasSuccessMetrics) {
        return;
      }
      
      // We need to add missing types
      let newInsightItems = [...insightItems];
      let hasChanges = false;
      
      // Add missing opportunities
      if (!hasOpportunities) {
        newInsightItems.push({
          id: `insight-opportunities-${Date.now()}`,
          type: 'opportunities',
          content: []
        });
        hasChanges = true;
      }
      
      // Add missing internal ownership
      if (!hasInternalOwnership) {
        newInsightItems.push({
          id: `insight-internalOwnership-${Date.now()}`,
          type: 'internalOwnership',
          content: []
        });
        hasChanges = true;
      }
      
      // Add missing success metrics
      if (!hasSuccessMetrics) {
        newInsightItems.push({
          id: `insight-successMetrics-${Date.now()}`,
          type: 'successMetrics',
          content: []
        });
        hasChanges = true;
      }
      
      // Update if we added any missing types
      if (hasChanges) {
        onInsightUpdate(newInsightItems);
        return;
      }
    } else {
      // No insightItems exist, initialize with empty arrays for all types
      let newInsightItems: InsightItem[] = [
        {
          id: `insight-opportunities-${Date.now()}`,
          type: 'opportunities',
          content: []
        },
        {
          id: `insight-internalOwnership-${Date.now()}`,
          type: 'internalOwnership',
          content: []
        },
        {
          id: `insight-successMetrics-${Date.now()}`,
          type: 'successMetrics',
          content: []
        }
      ];
      let hasChanges = true;
      
      // If we have legacy opportunities, convert them
      if (opportunities && opportunities.trim()) {
        // Convert string-based opportunities to array of objects
        const opportunitiesArray: Opportunity[] = opportunities.split('\n')
          .filter(text => text.trim())
          .map(text => ({
            id: `opportunity-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
            text: text.trim()
          }));
        
        if (opportunitiesArray.length > 0) {
          // Replace the empty opportunities with populated ones
          newInsightItems[0].content = opportunitiesArray;
        }
      }
      
      // If we've added any items, update the insights
      if (hasChanges) {
        onInsightUpdate(newInsightItems);
      }
    }
  }, [opportunities, insightItems, onInsightUpdate]);
  
  // Handle insight updates
  const handleInsightUpdate = (updatedInsightItems: InsightItem[]) => {
    onInsightUpdate(updatedInsightItems);
    
    // Update legacy opportunities field if relevant insight exists
    if (onOpportunitiesChange) {
      const opportunityItem = updatedInsightItems.find(item => item.type === 'opportunities');
      if (opportunityItem && Array.isArray(opportunityItem.content)) {
        const opportunityString = opportunityItem.content.map((opp: Opportunity) => opp.text).join('\n');
        onOpportunitiesChange(opportunityString);
      }
    }
  };
  
  return (
    <div className="space-y-4">
      <EditableField
        label="Gaps"
        value={gaps}
        placeholder="Click to add user pain points, frustrations, and challenges..."
        tooltipText="Document the main gaps or pain points across the journey"
        onChange={onGapsChange}
        lines={3}
      />
      
      <DynamicInsightField 
        insightItems={Array.isArray(insightItems) ? insightItems : []}
        onInsightUpdate={handleInsightUpdate}
      />
    </div>
  );
};

export default InsightsContent; 